import { Injectable } from '@angular/core';
import { NavigationEnd, Router, } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store, } from '@ngrx/store';
import { isAuthorizedInKeycloak } from 'app/keycloak/reducers/keycloak.reducer';
import { asyncScheduler, EMPTY, Observable, of, scheduled, zip, } from 'rxjs';
import { catchError, filter, first, map, pluck, switchMap, withLatestFrom, } from 'rxjs/operators';
import { ClearFacetsLockInStorage } from 'search/actions/facet.actions';
import { collapseBookshelf } from 'user/actions/user-profile.actions';
import { FormatGroup, AvailabilityStatus as V2Status } from '../../entity/models/entity';
import { ElementTriggeredSearch } from 'search/reducers/search.reducer';
import { getUserId } from 'user/reducers/user.reducer';
import {
  resetEntities,
  resetRollupTabsSelected,
  rollupTabSelected,
  setEntities,
  setSelectedTab
} from '../../entity/actions/entity.actions';
import { getEntities } from '../../entity/reducers/entity.reducer';
import { tryToOpenSaveSearchModal } from '../../saved-search/actions/saved-search.actions';
import {
  focusResultsNumberButton,
  focusSortedByButton,
  resetElementTriggeredSearch,
  SEARCH_RESULTS_LOAD,
  SEARCH_RESULTS_LOAD_COMPLETE,
  SEARCH_RESULTS_RESET,
  SearchActions,
  SearchResultsLoadAction,
  SearchResultsLoadCompleteAction,
  SearchResultsLoadFailureAction,
  setAdvancedMode,
  updateAdvancedMode,
} from '../actions/search.actions';
import { getAdvancedMode, getElementTriggeredSearch, getSearchObject, getSearchRequestBody, State, } from '../reducers/root.reducer';
import { SearchService } from '../services/search.service';

interface TabMatch {
  isAvailable: boolean;
  types: string[];
}

@Injectable()
export class SearchEffects {

  public searchByText: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(SEARCH_RESULTS_LOAD),
    map((action: SearchResultsLoadAction) => action.payload),
    withLatestFrom(
      this.store.pipe(select(isAuthorizedInKeycloak)),
      this.store.pipe(select(getSearchObject)),
      this.store.pipe(select(getSearchRequestBody)),
      this.store.pipe(select(getUserId)),
    ),
    map(([_, isAuthorized, searchObject, searchRequestBody, userId]) => {
        return {
          isAuthorized,
          searchObject,
          searchRequestBody,
          userId,
        };
      },
    ),
    switchMap(({searchObject, isAuthorized, searchRequestBody, userId}) => {
        if (!isAuthorized || (isAuthorized && userId)) {
          return this.searchService.getSearchResults(searchRequestBody).pipe(
            map((results) => new SearchResultsLoadCompleteAction({searchObject, results})),
            catchError((err) => of(new SearchResultsLoadFailureAction(err))));
        } else {
          return EMPTY;
        }
      },
    )));

  public rollupResultsLoaded = createEffect(() => this.actions$.pipe(
    ofType(SEARCH_RESULTS_LOAD_COMPLETE),
    map((action: SearchResultsLoadCompleteAction) => action.payload),
    switchMap(({searchObject, results}) => {
      const materialTypeIdsFromQuery = searchObject.filters.facets.materialTypeIds as string[];

      const actions = results.data?.reduce((acc, item) => {
        if (item.materialTabs) {
          const newTabIndex = this.getNewTabIndex(this.getTabMatches(item), materialTypeIdsFromQuery);
          return acc.concat(rollupTabSelected({
            tab: newTabIndex,
            resourceId: item.id,
          }));
        }

        return acc;
      }, []);

      actions.push(setEntities({entities: results.data}));

      return actions;
    }),
  ));

  public returnAuthenticatedAndOpenSaveSearchModal$ = createEffect(() =>
    zip(
      this.router.events.pipe(
        first((event) => event instanceof NavigationEnd),
        map(() => this.router.parseUrl(this.router.url)),
        pluck('queryParams'),
        filter(({openSaveSearchModal}) => !!openSaveSearchModal),
      ),
      this.actions$.pipe(ofType(SEARCH_RESULTS_LOAD_COMPLETE)),
    ).pipe(
      switchMap(() => scheduled(
        of(tryToOpenSaveSearchModal()),
        asyncScheduler,
      )),
    ));

  public checkSearchResultsAvailabilityRollUpCheckCompleteAction$ = createEffect(() => this.actions$.pipe(
    ofType(setSelectedTab),
    map(({resourceId}) => resourceId),
    withLatestFrom(this.store.pipe(select(getEntities)), this.store.pipe(select(getSearchObject))),
    switchMap(([resourceId, entities, searchObject]) => {
      const matches = this.getResultTabMatches(resourceId, entities);
      const query = searchObject && searchObject.filters.facets.materialTypeIds;
      const selectedIndex = matches && this.getNewTabIndex(matches, query as string[]);

      if (selectedIndex >= 0) {
        return of(rollupTabSelected({
          tab: selectedIndex,
          resourceId,
        }));
      }

      return EMPTY;
    }),
  ));

  public focusButtonTriggeredSearch$ = createEffect(() => this.actions$.pipe(
    ofType(SEARCH_RESULTS_LOAD_COMPLETE),
    withLatestFrom(
      this.store.pipe(select(getElementTriggeredSearch)),
    ),
    switchMap(([, elementTriggeredSearch]) => {
      const actions: Action[] = [];
      if (elementTriggeredSearch === ElementTriggeredSearch.RESULTS_NUMBER_BUTTON) {
        actions.push(focusResultsNumberButton());
      } else if (elementTriggeredSearch === ElementTriggeredSearch.SORTED_BY_BUTTON) {
        actions.push(focusSortedByButton());
      }
      if (elementTriggeredSearch !== ElementTriggeredSearch.NONE) {
        actions.push(resetElementTriggeredSearch());
      }
      return actions;
    }),
  ));

  public resetSelectedTabOnSearchResultReset$ = createEffect(() => this.actions$.pipe(
    ofType(SEARCH_RESULTS_RESET),
    switchMap(() => ([
      resetRollupTabsSelected(),
      resetEntities(),
    ])),
  ));

  public setAdvancedMode$ = createEffect(() => this.actions$.pipe(
    ofType(setAdvancedMode),
      withLatestFrom(
          this.store.pipe(select(getAdvancedMode)),
      ),
      switchMap(([action, preMode]) => {
        const actions: Action[] = [];
        if (action.advancedMode !== preMode) {
          actions.push(collapseBookshelf());
          actions.push(updateAdvancedMode({advancedMode: action.advancedMode}));
          actions.push(new ClearFacetsLockInStorage());
        }
        return actions;
      }),
  ));

  constructor(
    private readonly actions$: Actions<SearchActions>,
    private readonly searchService: SearchService,
    private readonly store: Store<State>,
    private readonly router: Router,
  ) {
  }

  private getResultTabMatches(resourceId: string, results: FormatGroup[]): TabMatch[] {
    const matchingItem = results.find((item) => item.id === resourceId);
    return this.getTabMatches(matchingItem);
  }

  private getTabMatches(item: FormatGroup) {
    return item?.materialTabs?.map((tab) => ({
      isAvailable: tab?.availability?.status?.general === V2Status.AVAILABLE,
      types: tab.materialTypes || [],
    }));
  }

  private getNewTabIndex(matches: TabMatch[], query: string[]): number {
    const flags = matches.map((item) => {
      let isMentionedInQuery = false;
      if (query && item.types.some((mType) => query.includes(mType))) {
        isMentionedInQuery = true;
      }
      return {
        isMentionedInQuery,
        isAvailable: item.isAvailable,
      };
    });

    let newTabIndex = 0;
    const fromQueryIndex = flags.findIndex((flag) => flag.isMentionedInQuery);
    const availableIndex = flags.findIndex((flag) => flag.isAvailable);

    if (fromQueryIndex > -1) {
      newTabIndex = fromQueryIndex;
    } else if (availableIndex > -1) {
      newTabIndex = availableIndex;
    }

    return newTabIndex;
  }
}
